/**
 * S-curve line chart.
 * Used by /reports/s-curve and the dashboard S-curve card.
 * Draws planned and actual cumulative percent. Does not fetch or recompute the series.
 */
import { Paper, Stack, Text } from '@mantine/core';
import { LineChart } from '@mantine/charts';
import type { SCurveChartRow } from '../logic/mapSCurveChart';
import { formatAmount, formatPercent, formatVariance } from './sCurveFormat';

const SERIES = [
  { name: 'planned', label: 'Planned', color: 'blue.6' },
  { name: 'actual', label: 'Actual', color: 'teal.6' },
];

function SCurveTooltip({
  label,
  payload,
  showPeriodAmounts,
}: {
  label?: string;
  payload?: ReadonlyArray<{ payload?: SCurveChartRow; value?: unknown }>;
  showPeriodAmounts: boolean;
}) {
  const row = payload?.[0]?.payload;
  if (!row) return null;
  return (
    <Paper px="sm" py="xs" withBorder shadow="md" radius="sm">
      <Text size="sm" fw={600} mb={4}>
        {label || row.periodLabel}
      </Text>
      <Stack gap={2}>
        {showPeriodAmounts ? (
          <>
            <Text size="xs">Planned this period: {formatAmount(row.plannedPeriod)}</Text>
            <Text size="xs">Actual this period: {formatAmount(row.actualPeriod)}</Text>
          </>
        ) : null}
        <Text size="xs">Planned cumulative: {formatAmount(row.plannedCumulative)}</Text>
        <Text size="xs">Actual cumulative: {formatAmount(row.actualCumulative)}</Text>
        <Text size="xs">Planned: {formatPercent(row.planned)}</Text>
        <Text size="xs">Actual: {formatPercent(row.actual)}</Text>
        <Text size="xs">Variance: {formatVariance(row.variance)}</Text>
      </Stack>
    </Paper>
  );
}

export default function SCurveChart({
  rows,
  showPeriodAmounts,
  h = 360,
}: {
  rows: SCurveChartRow[];
  showPeriodAmounts: boolean;
  h?: number;
}) {
  return (
    <LineChart
      h={h}
      data={rows}
      dataKey="periodLabel"
      series={SERIES}
      curveType="linear"
      // Mantine defaults this to true, which would draw Actual across the nulls after asOf.
      connectNulls={false}
      withDots={rows.length <= 36}
      withLegend
      yAxisProps={{ domain: [0, 100] }}
      xAxisProps={{ minTickGap: 28 }}
      valueFormatter={(value) => formatPercent(value)}
      gridAxis="xy"
      tooltipProps={{
        content: ({ label, payload }) => (
          <SCurveTooltip
            label={label == null ? undefined : String(label)}
            payload={payload as ReadonlyArray<{ payload?: SCurveChartRow; value?: unknown }> | undefined}
            showPeriodAmounts={showPeriodAmounts}
          />
        ),
      }}
    />
  );
}
